import React, {useState, useEffect, useRef, useCallback, useContext} from 'react';
import {Tag, Tooltip} from '@blueprintjs/core';
import styled from 'styled-components';
import {DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors} from '@dnd-kit/core';
import {SortableContext, verticalListSortingStrategy, horizontalListSortingStrategy, useSortable, arrayMove} from '@dnd-kit/sortable';
import {restrictToVerticalAxis, restrictToHorizontalAxis} from '@dnd-kit/modifiers';

import {EditModeCard, RemoveButton, headingFor, ItemLabel, EditableTitle, EditableDescription} from './item-card';
import {CollectionGroupContext} from './shared';

const SENSOR_OPTIONS = {activationConstraint: {distance: 5}};
const V_MODIFIERS = [restrictToVerticalAxis];
const H_MODIFIERS = [restrictToHorizontalAxis];


const ArrayWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--sf-vertical-gap);
`;

const HorizontalItems = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: var(--sf-horizontal-gap);
    & > * { min-width: 0;
`;


const ArrayError = styled.div`
    color: #cd4246;
    font-size: 12px;
    margin-top: 4px;
`;

const noLayoutAnimation = () => false;

const SortableItem = ({id, horizontal, children}) => {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id, animateLayoutChanges: noLayoutAnimation});
    const style = {
        transform: transform
            ? horizontal
                ? `translate3d(${transform.x}px, 0, 0)`
                : `translate3d(0, ${transform.y}px, 0)`
            : undefined,
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    return (
        <div ref={setNodeRef} style={{...style, outline: 'none'}} {...attributes}>
            {children(listeners, isDragging)}
        </div>
    );
};

const ArrayField = ({value, nullable, disabled, readonly, reorderable = true, horizontal, editMode: editModeProp, initialCollapsed, maxItems, itemSchema, depth, itemTitle, itemTitleReadOnly, itemTitleType, itemDescription, itemDescriptionReadOnly, itemDescriptionType, panelWidth, itemErrors, error, renderItem, onChange}) => {
    const editMode = editModeProp || (initialCollapsed != null ? 'collapsible' : undefined)
        || (!itemTitle && !itemDescription ? 'compact' : undefined);
    const items = Array.isArray(value) ? value : [];
    const canDrag = reorderable && !disabled && !readonly;
    const isHorizontal = !!horizontal;

    // Stable IDs for sortable items — indices shift on reorder, so we need persistent IDs
    const counterRef = useRef(0);
    const idsRef = useRef([]);
    // Sync idsRef length with items, assigning new IDs for any new slots
    if (idsRef.current.length !== items.length) {
        const newIds = [];
        for (let i = 0; i < items.length; i++) {
            newIds.push(idsRef.current[i] ?? `item-${counterRef.current++}`);
        }
        idsRef.current = newIds;
    }

    const sensors = useSensors(
        useSensor(MouseSensor, SENSOR_OPTIONS),
        useSensor(TouchSensor, SENSOR_OPTIONS),
    );

    // Collapse state lifted to survive canDrag branch changes (readonly toggle)
    const [collapsedMap, setCollapsedMap] = useState({});
    const isItemCollapsed = (id) => id in collapsedMap ? collapsedMap[id] : !!initialCollapsed;
    const toggleItemCollapse = useCallback((id) => {
        setCollapsedMap(prev => ({
            ...prev,
            [id]: !(id in prev ? prev[id] : !!initialCollapsed)
        }));
    }, [initialCollapsed]);

    const handleDragEnd = useCallback((event) => {
        const {active, over} = event;
        if (!over || active.id === over.id) return;
        const oldIndex = idsRef.current.indexOf(active.id);
        const newIndex = idsRef.current.indexOf(over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        idsRef.current = arrayMove(idsRef.current, oldIndex, newIndex);
        const reordered = arrayMove(items, oldIndex, newIndex);
        onChange(reordered);
    }, [items, onChange]);

    const collectionCtx = useContext(CollectionGroupContext);

    // Ref so the header's stale addTag closure always reads the latest value
    const valueRef = useRef(value);
    valueRef.current = value;

    const handleAdd = useCallback(() => {
        const currentItems = Array.isArray(valueRef.current) ? valueRef.current : [];
        const defaults = {};
        if (itemSchema?.properties) {
            for (const [key, prop] of Object.entries(itemSchema.properties)) {
                if (prop.default !== undefined) defaults[key] = prop.default;
            }
        }
        const newId = `item-${counterRef.current++}`;
        idsRef.current = [...idsRef.current, newId];
        if (editMode === 'collapsible') {
            setCollapsedMap(prev => ({...prev, [newId]: false}));
        }
        onChange([...currentItems, defaults]);
    }, [itemSchema, editMode, onChange]);

    const handleRemove = (index) => {
        idsRef.current = idsRef.current.filter((_, i) => i !== index);
        const remaining = items.filter((_, i) => i !== index);
        onChange(nullable && remaining.length === 0 ? null : remaining);
    };

    const handleItemChange = (index, newItem) => {
        const updated = items.map((item, i) => i === index ? newItem : item);
        onChange(updated);
    };

    const titleError = (index) => {
        if (!itemTitle || !itemErrors) return undefined;
        const errs = itemErrors(index);
        return errs.find(e => e.instancePath === '/' + itemTitle);
    };

    const descriptionError = (index) => {
        if (!itemDescription || !itemErrors) return undefined;
        const errs = itemErrors(index);
        return errs.find(e => e.instancePath === '/' + itemDescription);
    };

    const itemTitleText = (item, index) =>
        (itemTitle && item?.[itemTitle]) ? String(item[itemTitle]) : `Item ${index + 1}`;

    const renderItemCard = (item, index, dragListeners, isDragging) => (
        <EditModeCard
            editMode={editMode}
            readonly={readonly}
            collapsed={isItemCollapsed(idsRef.current[index])}
            onToggleCollapse={() => toggleItemCollapse(idsRef.current[index])}
            errors={itemErrors ? itemErrors(index) : []}
            panelWidth={panelWidth}
            dragListeners={dragListeners}
            description={itemDescription ? (
                <EditableDescription
                    value={item[itemDescription]}
                    disabled={disabled || readonly || itemDescriptionReadOnly}
                    error={descriptionError(index)}
                    type={itemDescriptionType}
                    depth={depth}
                    placeholder={`(Edit ${itemDescription})`}
                    onChange={(newVal) => handleItemChange(index, {...item, [itemDescription]: newVal})}
                />
            ) : undefined}
            depth={depth}
            titleText={itemTitleText(item, index)}
            title={itemTitle ? (
                <EditableTitle
                    value={item[itemTitle]}
                    disabled={disabled || readonly || itemTitleReadOnly}
                    error={titleError(index)}
                    type={itemTitleType}
                    depth={depth}
                    placeholder={`(Edit ${itemTitle})`}
                    onChange={(newVal) => handleItemChange(index, {...item, [itemTitle]: newVal})}
                />
            ) : (editMode === 'drawer' || editMode === 'overlay' || editMode === 'popover') ? (
                <ItemLabel as={headingFor(depth)}>Item {index + 1}</ItemLabel>
            ) : null}
            actions={!disabled && !readonly && (
                <Tooltip content={`Remove ${itemTitleText(item, index)}`} placement="top" hoverOpenDelay={400}>
                    <RemoveButton
                        icon="cross"
                        minimal
                        small
                        onClick={() => handleRemove(index)}
                    />
                </Tooltip>
            )}
        >
            {renderItem(item, index, (newItem) => handleItemChange(index, newItem))}
        </EditModeCard>
    );

    const canAdd = !disabled && !readonly && (!maxItems || items.length < maxItems);
    const addTag = canAdd ? <Tag icon="plus" round minimal interactive intent="primary" onClick={() => { collectionCtx?.expand(); handleAdd(); }}>Add</Tag> : null;
    const countLabel = maxItems ? `${items.length}/${maxItems} items` : `${items.length} items`;
    const headerKey = `${!!error}|${items.length}|${disabled}|${readonly}|${maxItems}`;
    useEffect(() => {
        if (collectionCtx) collectionCtx.setHeaderExtra(headerKey, (
            <>
                {error && <ArrayError>{error.message}</ArrayError>}
                <Tag minimal round>{countLabel}</Tag>
                {addTag}
            </>
        ));
    }, [headerKey]);

    return (
        <ArrayWrapper>
            {canDrag ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={isHorizontal ? H_MODIFIERS : V_MODIFIERS} onDragEnd={handleDragEnd}>
                    {isHorizontal ? (
                        <HorizontalItems>
                            <SortableContext items={idsRef.current} strategy={horizontalListSortingStrategy}>
                                {items.map((item, index) => (
                                    <SortableItem key={idsRef.current[index]} id={idsRef.current[index]} horizontal>
                                        {(listeners, isDragging) => renderItemCard(item, index, listeners, isDragging)}
                                    </SortableItem>
                                ))}
                            </SortableContext>
                        </HorizontalItems>
                    ) : (
                        <div>
                            <SortableContext items={idsRef.current} strategy={verticalListSortingStrategy}>
                                {items.map((item, index) => (
                                    <SortableItem key={idsRef.current[index]} id={idsRef.current[index]}>
                                        {(listeners, isDragging) => renderItemCard(item, index, listeners, isDragging)}
                                    </SortableItem>
                                ))}
                            </SortableContext>
                        </div>
                    )}
                </DndContext>
            ) : (
                isHorizontal ? (
                    <HorizontalItems>
                        {items.map((item, index) => (
                            <div key={index}>
                                {renderItemCard(item, index, null)}
                            </div>
                        ))}
                    </HorizontalItems>
                ) : (
                    items.map((item, index) => (
                        <div key={index}>
                            {renderItemCard(item, index, null)}
                        </div>
                    ))
                )
            )}
        </ArrayWrapper>
    );
};

export {ArrayField};
